import React, { useState, useEffect } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import store, { experienceActions } from '@apply/adminStore';
import { useSelector } from 'react-redux';
import { ExperienceApi, BorrowerApi } from '@apply/api';
import { applicationInsights } from '../../ApplicationInsights';
import { RefreshEvents } from '../../common/SharedEvents';
import { handleAdminError, handleAdminSuccess, handleAdminWarning } from '../../common/SharedFunctions';
import { LoadPanel } from 'devextreme-react/load-panel';
import { StateSelect, ZipCodeInput, CountySelect, InformModal, BfiCustomStore } from '../../common/SharedComponents';
import {
    ExperienceProjectTypeOptions,
    ExperiencePropertyTwoToFourUnitStructureType,
    ExperiencePropertyType,
    ExperiencePropertyTypeOptions,
    ExperiencePropertyTwoToFourUnitStructureTypeOptions,
    ExperiencePropertyTypeCategory,
    ExperienceRoleType,
    ExperienceRoleTypeOptions,
    ExperienceProjectType,
    ExperienceVerificationStatusType,
    ExperienceVerificationStatusTypeOptions,
    ExperienceDateType,
} from '@apply/constants';
import { NumericFormat } from 'react-number-format';
import { ChangeReasonModal } from '../common/components/ChangeReasonModal';
import { SuggestedVerificationWebsiteLinkModal } from '../modals/SuggestedVerificationWebsiteLinkModal';
import { PermissionType } from '@apply/constants';
import { hasPermission, YearInput } from '@apply/ui';
import DateBox from 'devextreme-react/date-box';

const NonBfiDetailModal = () => {
    const { isOpen, id, principalId } = useSelector((state) => state.experiences?.nonBfiDetailModal);
    const { dispatch } = store;
    const refreshEvents = new RefreshEvents();
    const experienceApi = new ExperienceApi.ExperienceApi(applicationInsights);
    const borrowerApi = new BorrowerApi.BorrowerApi(applicationInsights);

    const canEdit = hasPermission(PermissionType.ExperienceEditor);

    const [loadingMessage, set_loadingMessage] = useState('');
    const [openAddressNotUnique, set_openAddressNotUnique] = useState(false);
    const [changesModalOpen, set_changesModalOpen] = useState(false);
    const [suggestedVerificationWebsiteModalOpen, set_suggestedVerificationWebsiteModalOpen] = useState(false);

    const [principal, set_principal] = useState(null);
    const [lender, set_lender] = useState('');
    const [experience, set_experience] = useState(null);
    const [experienceJson, set_experienceJson] = useState('');
    const [projectTypeId, set_projectTypeId] = useState('');
    const [propertyTypeId, set_propertyTypeId] = useState('');
    const [twoToFourUnitTypeId, set_twoToFourUnitTypeId] = useState('');
    const [unitCount, set_unitCount] = useState('');
    const [street1, set_street1] = useState('');
    const [street2, set_street2] = useState('');
    const [city, set_city] = useState('');
    const [state, set_state] = useState('');
    const [zipCode, set_zipCode] = useState('');
    const [county, set_county] = useState('');
    const [stillOwn, set_stillOwn] = useState('');
    const [dateSold, set_dateSold] = useState('');
    const [dateSoldTypeId, set_dateSoldTypeId] = useState(ExperienceDateType.Exact);
    const [salesPrice, set_salesPrice] = useState('');
    const [verificationStatusTypeId, set_verificationStatusTypeId] = useState(ExperienceVerificationStatusType.Submitted);
    const [verificationNotes, set_verificationNotes] = useState('');
    const [verifiedDate, set_verifiedDate] = useState('');
    const [verifiedById, set_verifiedById] = useState('');
    const [conditionedSF, set_conditionedSF] = useState('');
    const [datePurchased, set_datePurchased] = useState('');
    const [purchasePrice, set_purchasePrice] = useState('');
    const [credits, set_credits] = useState('');
    const [notes, set_notes] = useState('');
    const [sourceWebsite, set_sourceWebsite] = useState('');
    const [sourceUrl, set_sourceUrl] = useState('');
    const [budget, set_budget] = useState('');
    const [roleTypeId, set_roleTypeId] = useState('');
    const [otherRoleName, set_otherRoleName] = useState('');
    const [homeConstructedAfterPurchase, set_homeConstructedAfterPurchase] = useState(false);
    const [commercialConsiderationAcknowledged, set_commercialConsiderationAcknowledged] = useState(false);

    const styles = {
        buttonMarginRight: { marginRight: '10px' },
        buttonContainer: { marginRight: '20px', textAlign: 'right' },
        label: {
            fontSize: '0.75rem',
            fontWeight: '500',
        },
        principalSummary: {
            padding: '10px',
            backgroundColor: '#e6e6e6',
            marginBottom: '10px',
            borderRadius: '5px',
        },
        principalName: {
            fontSize: 'larger',
            color: '#2c5591',
            fontWeight: 'bold',
        },
        credits: {
            backgroundColor: 'linen',
            textAlign: 'center',
            cursor: 'auto',
        },
    };

    const loadExperience = async (id) => {
        if (!id) {
            return;
        }

        try {
            set_loadingMessage('Loading Experience...');
            const response = await experienceApi.getExperienceNonBfi(id);
            if (!response || response.statusCode !== 200) {
                return;
            }

            set_experience(response.result);
        } finally {
            set_loadingMessage('');
        }
    };

    const loadPrincipal = async (principalId) => {
        if (!principalId) {
            return;
        }

        try {
            set_loadingMessage('Loading Principal...');

            const response = await borrowerApi.getPrincipalSummaryById(principalId);
            if (!response || response.statusCode !== 200) {
                return;
            }

            set_principal(response.result);
        } finally {
            set_loadingMessage('');
        }
    };

    const getModel = (exp) => {
        if (exp) {
            return {
                id: exp.id,
                lender: exp.lender,
                principalId: exp.principalId,
                projectTypeId: exp.projectTypeId,
                propertyTypeId: exp.propertyTypeId,
                twoToFourUnitTypeId: exp.twoToFourUnitTypeId,
                address: {
                    street1: experience.address.street1,
                    street2: experience.address.street2,
                    city: experience.address.city,
                    state: experience.address.state,
                    zipCode: experience.address.zipCode,
                    county: experience.address.county,
                },
                stillOwn: experience.stillOwn,
                dateSold: experience.dateSold ? new Date(experience.dateSold).toISOString().split('T')[0] : null,
                dateSoldTypeId: experience.dateSoldTypeId,
                salesPrice: experience.salesPrice ? parseFloat(experience.salesPrice) : null,
                datePurchased: experience.datePurchased ? new Date(experience.datePurchased).toISOString().split('T')[0] : null,
                purchasePrice: experience.purchasePrice ? parseFloat(experience.purchasePrice) : null,
                notes: experience.notes ? experience.notes : null,
                sourceWebsite: experience.sourceWebsite ? experience.sourceWebsite : null,
                sourceUrl: experience.sourceUrl ? experience.sourceUrl : null,
                verificationStatusTypeId: experience.verificationStatusTypeId,
                verificationNotes: experience.verificationNotes,
                verifiedDate: experience.verifiedDate,
                verifiedById: experience.verifiedById,
                conditionedSF: experience.conditionedSF ? parseInt(experience.conditionedSF) : null,
                budget: experience.budget ? parseFloat(experience.budget) : null,
                unitCount: experience.unitCount,
                roleTypeId: experience.roleTypeId,
                otherRoleName: experience.otherRoleName,
                homeConstructedAfterPurchase: experience.homeConstructedAfterPurchase,
                commercialConsiderationAcknowledged: experience.commercialConsiderationAcknowledged,
            };
        } else {
            return {
                id: experience?.id,
                lender,
                principalId: experience?.principalId ?? principal.id,
                projectTypeId,
                propertyTypeId,
                twoToFourUnitTypeId,
                address: {
                    street1,
                    street2,
                    city,
                    state,
                    zipCode,
                    county,
                },
                stillOwn: stillOwn === 1 ? true : stillOwn === 0 ? false : null,
                dateSold: dateSold ? new Date(dateSold).toISOString().split('T')[0] : null,
                dateSoldTypeId: dateSoldTypeId,
                salesPrice: salesPrice ? parseFloat(salesPrice) : null,
                datePurchased: datePurchased ? new Date(datePurchased).toISOString().split('T')[0] : null,
                purchasePrice: purchasePrice ? parseFloat(purchasePrice) : null,
                notes: notes ? notes : null,
                sourceWebsite: sourceWebsite ? sourceWebsite : null,
                sourceUrl: sourceUrl ? sourceUrl : null,
                verificationStatusTypeId,
                verificationNotes,
                verifiedDate,
                verifiedById,
                credits: credits ? parseInt(credits) : null,
                conditionedSF: conditionedSF ? parseInt(conditionedSF) : null,
                budget: budget ? parseFloat(budget) : null,
                unitCount,
                roleTypeId,
                otherRoleName,
                homeConstructedAfterPurchase,
                commercialConsiderationAcknowledged,
            };
        }
    };

    useEffect(() => {
        resetState();

        if (!id && !principalId) {
            return;
        }

        if (!id && principalId) {
            loadPrincipal(principalId);
        } else if (id) {
            loadExperience(id);
        }
    }, [id, principalId]);

    useEffect(() => {
        if (!experience) {
            return;
        }

        setState(experience);
        const model = getModel(experience);
        set_experienceJson(JSON.stringify(model));
        loadPrincipal(experience.principalId);
    }, [experience]);

    useEffect(() => {
        const getCreditsAsync = async () => {
            const cr = await getCredits();
            if (credits !== cr) {
                set_credits(cr);
            }
        };
        getCreditsAsync();
    }, [verificationStatusTypeId, roleTypeId, projectTypeId, propertyTypeId, twoToFourUnitTypeId, unitCount, budget]);

    const setState = (experience) => {
        if (!experience) {
            return;
        }

        set_lender(experience.lender);
        set_projectTypeId(experience.projectTypeId);
        onPropertyTypeChanged(experience.propertyTypeId);
        onTwoFourTypeChanged(experience.twoToFourUnitTypeId);
        set_unitCount(experience.unitCount);
        set_street1(experience.address.street1);
        set_street2(experience.address.street2);
        set_city(experience.address.city);
        set_state(experience.address.state);
        set_zipCode(experience.address.zipCode);
        set_county(experience.address.county);
        set_stillOwn(experience.stillOwn === null ? null : experience.stillOwn ? 1 : 0);
        set_dateSold(experience.dateSold ? new Date(experience.dateSold).toISOString().split('T')[0] : '');
        set_dateSoldTypeId(experience.dateSoldTypeId ? experience.dateSoldTypeId : ExperienceDateType.Exact);
        set_salesPrice(experience.salesPrice ?? '');
        set_verificationStatusTypeId(experience.verificationStatusTypeId);
        set_verificationNotes(experience.verificationNotes);
        set_verifiedDate(experience.verifiedDate);
        set_verifiedById(experience.verifiedById);
        set_conditionedSF(experience.conditionedSF);
        set_datePurchased(experience.datePurchased ? new Date(experience.datePurchased).toISOString().split('T')[0] : '');
        set_purchasePrice(experience.purchasePrice ?? '');
        set_notes(experience.notes);
        set_sourceWebsite(experience.sourceWebsite);
        set_sourceUrl(experience.sourceUrl);
        set_budget(experience.budget);
        set_roleTypeId(experience.roleTypeId);
        set_otherRoleName(experience.roleTypeId === ExperienceRoleType.Other ? experience.otherRoleName : '');
        set_homeConstructedAfterPurchase(experience.homeConstructedAfterPurchase);
        set_commercialConsiderationAcknowledged(experience.commercialConsiderationAcknowledged);
    };

    const resetState = () => {
        set_lender('');
        set_experience(null);
        set_experienceJson('');
        set_projectTypeId('');
        set_propertyTypeId('');
        set_twoToFourUnitTypeId('');
        set_unitCount('');
        set_street1('');
        set_street2('');
        set_city('');
        set_state('');
        set_zipCode('');
        set_county('');
        set_stillOwn('');
        set_dateSold('');
        set_dateSoldTypeId(ExperienceDateType.Exact);
        set_salesPrice('');
        set_verificationStatusTypeId(ExperienceVerificationStatusType.Submitted);
        set_verificationNotes('');
        set_verifiedDate('');
        set_verifiedById('');
        set_conditionedSF('');
        set_datePurchased('');
        set_purchasePrice('');
        set_credits('');
        set_notes('');
        set_sourceWebsite('');
        set_sourceUrl('');
        set_budget('');
        set_roleTypeId('');
        set_otherRoleName('');
        set_homeConstructedAfterPurchase(false);
        set_commercialConsiderationAcknowledged(false);
    };

    const _handleClose = () => {
        dispatch(
            experienceActions.setNonBfiDetailModal({
                nonBfiDetailModal: { isOpen: false },
            })
        );
    };

    const saveChanges = async (changeInfo) => {
        if (!canEdit) {
            handleAdminWarning('You do not have rights to edit this information');
            return;
        }

        try {
            set_loadingMessage('Saving...');

            const model = getModel();
            if (changeInfo) {
                model.changeInfo = changeInfo;
            }

            const response = await experienceApi.updateExperienceNonBfi(model);
            if (response.statusCode !== 200) {
                return;
            }

            refreshEvents.dispatchRefreshEvent();

            handleAdminSuccess('Changes saved successfully!');

            setTimeout(() => {
                _handleClose();
            }, 600);
        } catch (ex) {
            handleAdminError(ex, 'An unexpected error occurred. Please try again.');
        } finally {
            set_loadingMessage('');
        }
    };

    const getCredits = async () => {
        if (!verificationStatusTypeId || verificationStatusTypeId !== ExperienceVerificationStatusType.Verified) {
            return null;
        }

        try {
            const model = {
                verificationStatusTypeId,
                roleTypeId,
                projectTypeId,
                propertyTypeId,
                twoToFourUnitTypeId,
                unitCount,
                budget,
            };

            const response = await experienceApi.getNonBfiCredits(model);
            if (response.statusCode !== 200) {
                return null;
            }

            return response.result;
        } catch (ex) {
            handleAdminError(ex, 'An unexpected error occurred calculating credits.');
            return null;
        }
    };

    const validateExperience = async () => {
        try {
            set_loadingMessage('Validating address...');

            const model = {
                id: experience?.id ?? 0,
                principalId: experience?.principalId ?? principal.id,
                address: {
                    street1,
                    street2,
                    city,
                    state,
                    zipCode,
                },
            };

            const response = await experienceApi.validateExperienceNonBfi(model);
            if (response.statusCode !== 200) {
                return false;
            }
            return response.result.isValid;
        } catch (ex) {
            handleAdminError(
                new Error('Failed to validate Experience data'),
                'Failed to validate Experience data. please try again;'
            );
            return false;
        } finally {
            set_loadingMessage('');
        }
    };

    const _submitChanges = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (!canEdit) {
            handleAdminWarning('You do not have rights to edit this information');
            return;
        }

        const isValid = await validateExperience();
        if (!isValid) {
            set_openAddressNotUnique(true);
            return;
        }

        if (experienceJson) {
            const newJson = JSON.stringify(getModel());
            if (newJson !== experienceJson) {
                set_changesModalOpen(true);
            } else {
                setTimeout(() => {
                    _handleClose();
                }, 600);
            }
            return;
        }

        await saveChanges();
    };

    const onPropertyTypeChanged = (id) => {
        set_propertyTypeId(id);

        if (id) {
            let units = 1;
            switch (id) {
                case ExperiencePropertyType.TwoToFourUnitStructure:
                    onTwoFourTypeChanged(twoToFourUnitTypeId);
                    break;
                case ExperiencePropertyType.FivePlusMultifamily:
                    units = 5;
                    break;
            }
            set_unitCount(units);
        }
    };

    const onTwoFourTypeChanged = (id) => {
        set_twoToFourUnitTypeId(id);

        if (id) {
            let units = 1;
            switch (id) {
                case ExperiencePropertyTwoToFourUnitStructureType.Four:
                    units = 4;
                    break;
                case ExperiencePropertyTwoToFourUnitStructureType.Three:
                    units = 3;
                    break;
                case ExperiencePropertyTwoToFourUnitStructureType.Two:
                    units = 2;
                    break;
            }
            set_unitCount(units);
        }
    };

    const onRoleTypeChanged = (id) => {
        set_roleTypeId(id);

        if (id) {
            switch (id) {
                case ExperienceRoleType.TwentyPercentPlusOwner:
                case ExperienceRoleType.GeneralContractor:
                    set_otherRoleName('');
                    break;
            }
        }
    };

    const onVerificationStatusTypeChanged = (id) => {
        set_verificationStatusTypeId(id);
        set_verificationNotes('');
    };

    const onStillOwnChanged = (val) => {
        set_stillOwn(val);
        if (val) {
            set_dateSold('');
            set_salesPrice('');
        }
    };

    const onYearSelection = (value) => {
        if (value && value !== '' && value != null) {
            let selectedDate = new Date(value).toISOString().split('T')[0];
            set_dateSold(selectedDate);
        } else {
            set_dateSold(null);
        }
    };

    const onDateSoldTypeChange = (value) => {
        set_dateSold(null);
        set_dateSoldTypeId(value);
    };

    const onChangesModalCancel = () => {
        set_changesModalOpen(false);
    };

    const onChangesModalConfirm = async (model) => {
        set_changesModalOpen(false);
        await saveChanges(model);
    };

    const onSuggestedWebsiteClick = () => {
        set_suggestedVerificationWebsiteModalOpen(true);
    };

    const onSuggestedVerificationWebsiteModalCancel = () => {
        set_suggestedVerificationWebsiteModalOpen(false);
    };

    const onSuggestedVerificationWebsiteModalConfirm = async (selectedSuggestions) => {
        set_suggestedVerificationWebsiteModalOpen(false);
        let currentWebsite = sourceWebsite;
        let currentWebURL = sourceUrl;
        selectedSuggestions.forEach((p) => {
            if (currentWebsite && currentWebsite.trimEnd().length > 0) {
                currentWebsite = currentWebsite + '\n' + p.sourceWebsite;
            } else {
                currentWebsite = p.sourceWebsite;
            }

            if (currentWebURL && currentWebURL.trimEnd().length > 0) {
                currentWebURL = currentWebURL + '\r' + p.sourceUrl;
            } else {
                currentWebURL = p.sourceUrl;
            }
        });

        set_sourceWebsite(currentWebsite);
        set_sourceUrl(currentWebURL);
    };

    if (!isOpen || (!id && !principal)) {
        return null;
    }

    return (
        <>
            <Dialog
                maxWidth='sm'
                open={isOpen}
                onClose={_handleClose}
                aria-labelledby='form-dialog-title'
            >
                <DialogContent>
                    <div className='modal-container'>
                        <div className='modal-backdrop'></div>
                        <div
                            className='modal'
                            tabIndex='-1'
                            role='dialog'
                            style={{ width: '950px' }}
                        >
                            <div
                                className='modal-dialog'
                                role='document'
                            >
                                <div>
                                    <div
                                        className='page-header'
                                        role='banner'
                                    >
                                        <h1>Experience Detail (Non-BFI)</h1>
                                        <button
                                            type='button'
                                            className='close'
                                            data-dismiss='modal'
                                            aria-label='Close'
                                            onClick={_handleClose}
                                        >
                                            <span aria-hidden='true'>&times;</span>
                                        </button>
                                    </div>
                                    <form
                                        name='experienceForm'
                                        className='standard-form'
                                        onSubmit={async (event) => await _submitChanges(event)}
                                    >
                                        <div
                                            className='scrollable-content'
                                            style={{ margin: '10px' }}
                                        >
                                            <div style={styles.principalSummary}>
                                                <span style={styles.principalName}>{principal && principal.name}</span>
                                            </div>
                                            <div
                                                style={{
                                                    padding: '0 10px 0 10px',
                                                }}
                                            >
                                                <div className='row'>
                                                    <div className='columns small-12 large-6'>
                                                        <label htmlFor='projectTypeId'>Project Type</label>
                                                        <select
                                                            id={'projectTypeId'}
                                                            value={projectTypeId ?? ''}
                                                            onChange={(event) => set_projectTypeId(parseInt(event.target.value))}
                                                            required={false}
                                                            disabled={!canEdit}
                                                        >
                                                            <option value=''></option>
                                                            {ExperienceProjectTypeOptions.map((o) => {
                                                                return (
                                                                    <option
                                                                        key={o.id}
                                                                        value={o.id}
                                                                    >
                                                                        {o.name}
                                                                    </option>
                                                                );
                                                            })}
                                                        </select>
                                                    </div>
                                                    <div className='columns small-12 large-3'>
                                                        <label htmlFor='budget'>
                                                            Budget{' '}
                                                            {projectTypeId === ExperienceProjectType.FixFlipRehab && (
                                                                <span className='req'>*</span>
                                                            )}
                                                        </label>
                                                        <NumericFormat
                                                            id='budget'
                                                            value={budget ?? ''}
                                                            decimalScale={2}
                                                            prefix='$'
                                                            thousandSeparator=','
                                                            allowNegative={false}
                                                            onValueChange={(values) => {
                                                                set_budget(values.floatValue);
                                                            }}
                                                            required={projectTypeId === ExperienceProjectType.FixFlipRehab}
                                                            isAllowed={(values, sourceInfo) => {
                                                                const value = values?.floatValue;
                                                                return (
                                                                    value === undefined ||
                                                                    value === null ||
                                                                    (value >= 0 && value <= 100000000)
                                                                );
                                                            }}
                                                            disabled={!canEdit}
                                                        />
                                                    </div>
                                                    {projectTypeId === ExperienceProjectType.PersonalResidence && (
                                                        <div
                                                            className='columns small-12 large-3'
                                                            style={{
                                                                paddingTop: '30px',
                                                            }}
                                                        >
                                                            <span style={styles.label}>
                                                                Constructed After Purchase?&nbsp;&nbsp;
                                                            </span>
                                                            <input
                                                                id='homeConstructedAfterPurchase'
                                                                type='checkbox'
                                                                checked={homeConstructedAfterPurchase}
                                                                onChange={(event) =>
                                                                    set_homeConstructedAfterPurchase(event.target.checked)
                                                                }
                                                                disabled={!canEdit}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                                <div className='row'>
                                                    <div className='columns small-12 large-4'>
                                                        <label htmlFor='roleTypeId'>Role</label>
                                                        <select
                                                            id={'roleTypeId'}
                                                            value={roleTypeId ?? ''}
                                                            onChange={(event) => onRoleTypeChanged(parseInt(event.target.value))}
                                                            required={false}
                                                            disabled={!canEdit}
                                                        >
                                                            <option value=''></option>
                                                            {ExperienceRoleTypeOptions.map((o) => {
                                                                return (
                                                                    <option
                                                                        key={o.id}
                                                                        value={o.id}
                                                                    >
                                                                        {o.name}
                                                                    </option>
                                                                );
                                                            })}
                                                        </select>
                                                    </div>
                                                    {roleTypeId === ExperienceRoleType.Other && (
                                                        <div className='columns small-12 large-8'>
                                                            <label htmlFor='roleTypeId'>
                                                                Role Name <span className='req'>*</span>
                                                            </label>
                                                            <input
                                                                id='otherRoleName'
                                                                type='text'
                                                                maxLength={100}
                                                                value={otherRoleName}
                                                                onChange={(event) => set_otherRoleName(event.target.value)}
                                                                required={true}
                                                                disabled={!canEdit}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                                <h2 className='decorated'>Property</h2>
                                                <div className='row'>
                                                    <div className='columns small-12 large-4'>
                                                        <label htmlFor='creditTypeId'>Property Type</label>
                                                        <select
                                                            id={'propertyTypeId'}
                                                            value={propertyTypeId ?? ''}
                                                            onChange={(event) =>
                                                                onPropertyTypeChanged(parseInt(event.target.value))
                                                            }
                                                            required={false}
                                                            disabled={!canEdit}
                                                        >
                                                            <option value=''></option>

                                                            {ExperiencePropertyTypeOptions.filter(
                                                                (item) =>
                                                                    item.category === ExperiencePropertyTypeCategory.CoreOptions
                                                            ).map((o) => {
                                                                return (
                                                                    <option
                                                                        key={o.id}
                                                                        value={o.id}
                                                                    >
                                                                        {o.name}
                                                                    </option>
                                                                );
                                                            })}
                                                            <option disabled={true}>──────────────────────</option>
                                                            {ExperiencePropertyTypeOptions.filter(
                                                                (item) => item.category === ExperiencePropertyTypeCategory.Other
                                                            ).map((o) => {
                                                                return (
                                                                    <option
                                                                        key={o.id}
                                                                        value={o.id}
                                                                    >
                                                                        {o.name}
                                                                    </option>
                                                                );
                                                            })}
                                                        </select>
                                                    </div>

                                                    {propertyTypeId === ExperiencePropertyType.TwoToFourUnitStructure && (
                                                        <div className='columns small-12 large-4'>
                                                            <label htmlFor='twoToFourUnitTypeId'>Type</label>
                                                            <select
                                                                id={'twoToFourUnitTypeId'}
                                                                value={twoToFourUnitTypeId ?? ''}
                                                                onChange={(event) =>
                                                                    onTwoFourTypeChanged(parseInt(event.target.value))
                                                                }
                                                                required={false}
                                                                disabled={!canEdit}
                                                            >
                                                                <option value=''></option>
                                                                {ExperiencePropertyTwoToFourUnitStructureTypeOptions.map((o) => {
                                                                    return (
                                                                        <option
                                                                            key={o.id}
                                                                            value={o.id}
                                                                        >
                                                                            {o.name}
                                                                        </option>
                                                                    );
                                                                })}
                                                            </select>
                                                        </div>
                                                    )}

                                                    {propertyTypeId === ExperiencePropertyType.Commercial && (
                                                        <div
                                                            className='columns small-12 large-8'
                                                            style={{
                                                                paddingTop: '30px',
                                                            }}
                                                        >
                                                            <span style={styles.label}>
                                                                Principal Acknowledged Commercial Experience may not be
                                                                considered?&nbsp;&nbsp;
                                                            </span>
                                                            <input
                                                                id='commercialConsiderationAcknowledged'
                                                                type='checkbox'
                                                                checked={commercialConsiderationAcknowledged}
                                                                onChange={(event) =>
                                                                    set_commercialConsiderationAcknowledged(event.target.checked)
                                                                }
                                                                disabled={!canEdit}
                                                            />
                                                        </div>
                                                    )}

                                                    {(propertyTypeId === ExperiencePropertyType.FivePlusMultifamily ||
                                                        propertyTypeId === ExperiencePropertyType.ResidentialMixedUse) && (
                                                        <div className='columns small-12 large-2'>
                                                            <label htmlFor='unitCount'>
                                                                Units <span className='req'>*</span>
                                                            </label>
                                                            <NumericFormat
                                                                id='unitCount'
                                                                value={unitCount ?? ''}
                                                                decimalScale={0}
                                                                allowNegative={false}
                                                                onValueChange={(values) => {
                                                                    set_unitCount(values.floatValue);
                                                                }}
                                                                required={true}
                                                                isAllowed={(values, sourceInfo) => {
                                                                    const value = values?.floatValue;
                                                                    return (
                                                                        value === undefined ||
                                                                        value === null ||
                                                                        (value >= 0 && value <= 100)
                                                                    );
                                                                }}
                                                                disabled={!canEdit}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                                <div className='row'>
                                                    <div className='columns small-12 large-8'>
                                                        <label htmlFor='street1'>
                                                            Street Address <span className='req'>*</span>
                                                        </label>
                                                        <input
                                                            id='street1'
                                                            type='text'
                                                            maxLength={100}
                                                            value={street1}
                                                            onChange={(event) => set_street1(event.target.value)}
                                                            required={true}
                                                            disabled={!canEdit}
                                                        />
                                                    </div>
                                                    <div className='columns small-12 large-4'>
                                                        <label htmlFor='street2'>Street Address 2</label>
                                                        <input
                                                            id='street2'
                                                            type='text'
                                                            maxLength={100}
                                                            value={street2}
                                                            onChange={(event) => set_street2(event.target.value)}
                                                            disabled={!canEdit}
                                                        />
                                                    </div>
                                                    <div className='columns small-12 large-6'>
                                                        <label htmlFor='city'>
                                                            City <span className='req'>*</span>
                                                        </label>
                                                        <input
                                                            id='city'
                                                            type='text'
                                                            maxLength={50}
                                                            value={city}
                                                            onChange={(event) => set_city(event.target.value)}
                                                            required={true}
                                                            disabled={!canEdit}
                                                        />
                                                    </div>
                                                    <div className='columns small-12 medium-6 large-3'>
                                                        <label htmlFor='state'>
                                                            State <span className='req'>*</span>
                                                        </label>
                                                        <StateSelect
                                                            id='state'
                                                            value={state}
                                                            onChange={(event) => set_state(event.target.value)}
                                                            required={true}
                                                            disabled={!canEdit}
                                                        />
                                                    </div>
                                                    <div className='columns small-12 medium-6 large-3'>
                                                        <label htmlFor='zipCode'>
                                                            Zip Code <span className='req'>*</span>
                                                        </label>
                                                        <ZipCodeInput
                                                            id='zipCode'
                                                            value={zipCode}
                                                            onChange={(event) => set_zipCode(event.target.value)}
                                                            required={true}
                                                            disabled={!canEdit}
                                                        />
                                                    </div>
                                                </div>
                                                <div className='row'>
                                                    <div className='columns small-12 large-4'>
                                                        <label htmlFor={'county'}>County</label>
                                                        <CountySelect
                                                            id={'county'}
                                                            value={county ?? ''}
                                                            selectedState={state ?? ''}
                                                            onChange={(event) => set_county(event.target.value)}
                                                            disabled={!canEdit || !state}
                                                        />
                                                    </div>
                                                    <div className='columns small-12 large-2'>
                                                        <label htmlFor={'stillOwn'}>Still Own?</label>
                                                        <select
                                                            id={'stillOwn'}
                                                            value={stillOwn ?? ''}
                                                            onChange={(event) => onStillOwnChanged(parseInt(event.target.value))}
                                                            required={false}
                                                            disabled={!canEdit}
                                                        >
                                                            <option value=''></option>
                                                            <option value='1'>Yes</option>
                                                            <option value='0'>No</option>
                                                        </select>
                                                    </div>
                                                    <div className='columns small-12 large-3'>
                                                        <label htmlFor='dateSold'>
                                                            Date Sold {stillOwn === 0 && <span className='req'>*</span>}
                                                        </label>
                                                        {dateSoldTypeId === ExperienceDateType.Exact && (
                                                            <DateBox
                                                                id='dateSold'
                                                                value={dateSold}
                                                                onValueChange={set_dateSold}
                                                                disabled={!canEdit || stillOwn !== 0}
                                                                required={stillOwn === 0}
                                                            />
                                                        )}
                                                        {dateSoldTypeId === ExperienceDateType.YearOnly && (
                                                            <YearInput
                                                                id='dateSold'
                                                                value={dateSold ? new Date(dateSold).getUTCFullYear() : null}
                                                                disabled={stillOwn}
                                                                onChange={(event) => onYearSelection(event.target.value)}
                                                                maxWidth='190px'
                                                            />
                                                        )}
                                                        <div
                                                            style={{
                                                                paddingTop: '5px',
                                                            }}
                                                        >
                                                            <label
                                                                htmlFor='dateSoldType-exact'
                                                                style={{
                                                                    float: 'left',
                                                                }}
                                                            >
                                                                <input
                                                                    type='radio'
                                                                    id='dateSoldType-exact'
                                                                    disabled={stillOwn}
                                                                    checked={dateSoldTypeId === ExperienceDateType.Exact}
                                                                    onChange={() =>
                                                                        onDateSoldTypeChange(ExperienceDateType.Exact)
                                                                    }
                                                                />
                                                                Exact Date
                                                            </label>
                                                            <label
                                                                htmlFor='dateSoldType-year'
                                                                style={{
                                                                    float: 'left',
                                                                }}
                                                            >
                                                                <input
                                                                    type='radio'
                                                                    id='dateSoldType-year'
                                                                    disabled={stillOwn}
                                                                    checked={dateSoldTypeId === ExperienceDateType.YearOnly}
                                                                    onChange={() =>
                                                                        onDateSoldTypeChange(ExperienceDateType.YearOnly)
                                                                    }
                                                                />
                                                                Year Only
                                                            </label>
                                                        </div>
                                                    </div>
                                                    <div className='columns small-12 large-3'>
                                                        <label htmlFor='salesPrice'>Sales Price </label>
                                                        <NumericFormat
                                                            id='salesPrice'
                                                            value={salesPrice ?? ''}
                                                            decimalScale={2}
                                                            prefix='$'
                                                            thousandSeparator=','
                                                            allowNegative={false}
                                                            onValueChange={(values) => {
                                                                set_salesPrice(values.floatValue);
                                                            }}
                                                            isAllowed={(values, sourceInfo) => {
                                                                const value = values?.floatValue;
                                                                return (
                                                                    value === undefined ||
                                                                    value === null ||
                                                                    (value >= 0 && value <= 100000000)
                                                                );
                                                            }}
                                                            disabled={!canEdit || stillOwn !== 0}
                                                        />
                                                    </div>
                                                </div>
                                                <div className='row'>
                                                    <div className='columns small-12 large-12'>
                                                        <label htmlFor='lender'>Lender (optional)</label>
                                                        <input
                                                            id='lender'
                                                            type='text'
                                                            maxLength={100}
                                                            value={lender}
                                                            onChange={(event) => set_lender(event.target.value)}
                                                            required={false}
                                                            disabled={!canEdit}
                                                        />
                                                    </div>
                                                </div>
                                                <h2 className='decorated'>Verification</h2>
                                                <div className='row'>
                                                    <div className='columns small-12 large-4'>
                                                        <label htmlFor='verificationStatusTypeId'>Verification Status:</label>
                                                        <select
                                                            id={'verificationStatusTypeId'}
                                                            value={verificationStatusTypeId}
                                                            onChange={(event) =>
                                                                onVerificationStatusTypeChanged(parseInt(event.target.value))
                                                            }
                                                            required={false}
                                                            disabled={!canEdit}
                                                        >
                                                            {ExperienceVerificationStatusTypeOptions.map((o) => {
                                                                return (
                                                                    <option
                                                                        key={o.id}
                                                                        value={o.id}
                                                                    >
                                                                        {o.name}
                                                                    </option>
                                                                );
                                                            })}
                                                        </select>
                                                    </div>
                                                    <div
                                                        className='columns small-12 large-7'
                                                        style={{
                                                            paddingTop: '32px',
                                                        }}
                                                    >
                                                        {verificationStatusTypeId &&
                                                        verificationStatusTypeId !== ExperienceVerificationStatusType.Submitted &&
                                                        experience &&
                                                        experience.verifiedByName ? (
                                                            <span>
                                                                &nbsp;&nbsp; Status updated by <b>{experience.verifiedByName}</b>{' '}
                                                                on {new Date(experience.verifiedDate).toLocaleDateString('en-us')}
                                                            </span>
                                                        ) : null}
                                                    </div>
                                                    <div className='columns small-12 large-1'>
                                                        <label htmlFor='credits'>Credits</label>
                                                        <input
                                                            type='text'
                                                            id='credits'
                                                            value={credits ?? ''}
                                                            required={false}
                                                            disabled={true}
                                                            style={styles.credits}
                                                            title='Credits are only calculated for Verified experience'
                                                        />
                                                    </div>
                                                </div>
                                                {verificationStatusTypeId &&
                                                (verificationStatusTypeId === ExperienceVerificationStatusType.UnableToVerify ||
                                                    verificationStatusTypeId === ExperienceVerificationStatusType.Rejected) ? (
                                                    <div
                                                        className='row'
                                                        style={styles.formRow}
                                                    >
                                                        <div className='columns small-12 large-12'>
                                                            <label htmlFor='notes'>Verification Notes:</label>
                                                            <textarea
                                                                id={'verificationNotes'}
                                                                cols={40}
                                                                rows={4}
                                                                maxLength={2000}
                                                                className='req'
                                                                value={verificationNotes ?? ''}
                                                                onChange={(e) => set_verificationNotes(e.target.value)}
                                                                disabled={
                                                                    !canEdit ||
                                                                    !verificationStatusTypeId ||
                                                                    verificationStatusTypeId ===
                                                                        ExperienceVerificationStatusType.UnableToVerify ||
                                                                    verificationStatusTypeId ===
                                                                        ExperienceVerificationStatusType.Rejected
                                                                }
                                                                placeholder='Enter relevant verification notes...'
                                                            />
                                                        </div>
                                                    </div>
                                                ) : null}
                                                <div className='row'>
                                                    <div className='columns small-12 large-2'>
                                                        <label htmlFor='conditionedSF'>Condition SF</label>
                                                        <NumericFormat
                                                            id='conditionedSF'
                                                            value={conditionedSF ?? ''}
                                                            decimalScale={0}
                                                            thousandSeparator=','
                                                            allowNegative={false}
                                                            onValueChange={(values) => {
                                                                set_conditionedSF(values.floatValue);
                                                            }}
                                                            required={false}
                                                            isAllowed={(values, sourceInfo) => {
                                                                const value = values?.floatValue;
                                                                return (
                                                                    value === undefined ||
                                                                    value === null ||
                                                                    (value >= 0 && value <= 1000000)
                                                                );
                                                            }}
                                                            disabled={!canEdit}
                                                        />
                                                    </div>
                                                    <div className='columns small-12 large-3'>
                                                        <label htmlFor='datePurchased'>Date Purchased</label>
                                                        <DateBox
                                                            id='datePurchased'
                                                            value={datePurchased}
                                                            onValueChange={set_datePurchased}
                                                            disabled={!canEdit}
                                                        />
                                                    </div>
                                                    <div className='columns small-12 large-2'>
                                                        <label htmlFor='purchasePrice'>Purchase Price</label>
                                                        <NumericFormat
                                                            id='purchasePrice'
                                                            value={purchasePrice ?? ''}
                                                            decimalScale={2}
                                                            prefix='$'
                                                            thousandSeparator=','
                                                            allowNegative={false}
                                                            onValueChange={(values) => {
                                                                set_purchasePrice(values.floatValue);
                                                            }}
                                                            required={false}
                                                            isAllowed={(values, sourceInfo) => {
                                                                const value = values?.floatValue;
                                                                return (
                                                                    value === undefined ||
                                                                    value === null ||
                                                                    (value >= 0 && value <= 100000000)
                                                                );
                                                            }}
                                                            disabled={!canEdit}
                                                        />
                                                    </div>
                                                </div>
                                                <div className='row'>
                                                    <div className='columns small-12 large-12'>
                                                        <div
                                                            style={{
                                                                float: 'left',
                                                            }}
                                                        >
                                                            <label htmlFor='sourceWebsite'>Source Website(s)</label>
                                                        </div>
                                                        <div
                                                            style={{
                                                                float: 'right',
                                                            }}
                                                        >
                                                            <button
                                                                style={styles.buttonMarginRight}
                                                                type='button'
                                                                className='button primary'
                                                                disabled={!canEdit || state === ''}
                                                                onClick={onSuggestedWebsiteClick}
                                                            >
                                                                View Suggestions
                                                            </button>
                                                        </div>
                                                        <textarea
                                                            id='sourceWebsite'
                                                            type='text'
                                                            maxLength={1000}
                                                            rows={3}
                                                            value={sourceWebsite}
                                                            onChange={(event) => set_sourceWebsite(event.target.value)}
                                                            placeholder='Descriptive name of the website, e.g Hudson County GIS'
                                                            disabled={!canEdit}
                                                        />
                                                    </div>
                                                    <div className='columns small-12 large-12'>
                                                        <label htmlFor='sourceUrl'>Source Link(s)</label>
                                                        <textarea
                                                            id='sourceUrl'
                                                            type='text'
                                                            maxLength={1000}
                                                            rows={3}
                                                            value={sourceUrl}
                                                            onChange={(event) => set_sourceUrl(event.target.value)}
                                                            placeholder='www.hudsoncounty.gov/properties/1/bla/here.html'
                                                            disabled={!canEdit}
                                                        />
                                                    </div>
                                                </div>
                                                <div className='row'>
                                                    <div className='columns small-12 large-12'>
                                                        <label htmlFor='notes'>Notes:</label>
                                                        <textarea
                                                            id={'notes'}
                                                            cols={40}
                                                            rows={4}
                                                            maxLength={2000}
                                                            className='req'
                                                            value={notes ?? ''}
                                                            onChange={(e) => set_notes(e.target.value)}
                                                            placeholder='Enter relevant experience notes...'
                                                            disabled={!canEdit}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div
                                            className='button-footer'
                                            style={styles.buttonContainer}
                                        >
                                            <button
                                                style={styles.buttonMarginRight}
                                                type='submit'
                                                className='button primary'
                                                disabled={!canEdit}
                                            >
                                                Save
                                            </button>
                                            <button
                                                type='button'
                                                className='button hollow'
                                                data-dismiss='modal'
                                                onClick={_handleClose}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            <LoadPanel
                hideOnOutsideClick={false}
                visible={!!loadingMessage}
                message={loadingMessage}
            />
            <InformModal
                isOpen={openAddressNotUnique}
                title='Duplicate Address!'
                text='Another experience for the same principal and address already exists.'
                confirmCallback={() => set_openAddressNotUnique(false)}
            />
            <ChangeReasonModal
                isOpen={changesModalOpen}
                onCancel={onChangesModalCancel}
                onSubmit={onChangesModalConfirm}
            />
            <SuggestedVerificationWebsiteLinkModal
                options={{
                    isOpen: suggestedVerificationWebsiteModalOpen,
                    stateCd: state ?? '',
                    county: county ?? '',
                    id: id,
                }}
                onCancel={onSuggestedVerificationWebsiteModalCancel}
                onSubmit={onSuggestedVerificationWebsiteModalConfirm}
            />
        </>
    );
};

export default NonBfiDetailModal;
